import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle, ArrowRight, FileCheck, Scale, Clock, Award, Users, Star, ChevronLeft, ChevronRight, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useContactPopup } from '@/hooks/useContactPopup';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';

// Service Form Component
const ServiceForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+44',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    submissions.push({ ...formData, timestamp: new Date().toISOString(), source: 'trademark-service-page' });
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));
    toast.success('Thank you! We\'ll contact you within 24 hours to discuss your trademark registration.');
    setFormData({ name: '', email: '', countryCode: '+44', phone: '', message: '' });
  };

  const countryCodes = [
    { code: '+44', country: 'UK' },
    { code: '+1', country: 'US/Canada' },
    { code: '+91', country: 'India' },
    { code: '+61', country: 'Australia' },
    { code: '+353', country: 'Ireland' }
  ];

  return (
    <Card className="p-8 border-2 border-primary shadow-elegant-xl">
      <div className="mb-6">
        <Badge className="mb-3 bg-secondary text-secondary-foreground">Special Offer</Badge>
        <h3 className="text-2xl font-bold text-foreground mb-2">Secure Your Trademark - Only £350 Yearly</h3>
        <p className="text-muted-foreground">Complete trademark registration with expert handling. Fill out the form and we'll contact you within 24 hours.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="service-name">Name *</Label>
          <Input
            id="service-name"
            placeholder="John Smith"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-email">Email *</Label>
          <Input
            id="service-email"
            type="email"
            placeholder="john@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-phone">Contact Number *</Label>
          <div className="flex gap-2">
            <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.code} ({item.country})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="service-phone"
              type="tel"
              placeholder="1234567890"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="flex-1"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-message">Trademark Details</Label>
          <Textarea
            id="service-message"
            placeholder="Tell us about your trademark (name, logo, class of goods/services, etc.)"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            rows={4}
          />
        </div>

        <Button type="submit" className="w-full bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300">
          Get Started - Register My Trademark
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </form>
    </Card>
  );
};

// Testimonial Card Component
const TestimonialCard = ({ testimonial }) => (
  <Card className="p-6 h-full flex flex-col border-border shadow-lg hover:shadow-xl transition-shadow duration-300">
    <div className="flex gap-1 mb-4">
      {[1, 2, 3, 4, 5].map((star) => {
        const rating = testimonial.rating;
        const isFullStar = star <= rating;
        const isHalfStar = star === Math.ceil(rating) && rating % 1 !== 0;
        
        return (
          <div key={star} className="relative w-5 h-5">
            {isHalfStar ? (
              <>
                <Star className="w-5 h-5 text-gray-300 absolute inset-0" fill="currentColor" />
                <div className="overflow-hidden absolute w-2.5 h-5">
                  <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                </div>
              </>
            ) : (
              <Star 
                className={`w-5 h-5 ${isFullStar ? 'text-yellow-400' : 'text-gray-300'}`} 
                fill="currentColor" 
              />
            )}
          </div>
        );
      })}
    </div>
    
    <p className="text-base text-foreground mb-6 leading-relaxed italic flex-1">
      &quot;{testimonial.content}&quot;
    </p>
    
    <div className="border-t border-border pt-4">
      <p className="font-semibold text-foreground">{testimonial.name}</p>
      <p className="text-sm text-muted-foreground mt-1">{testimonial.role}</p>
    </div>
  </Card>
);

export const TrademarkRegistrationPage = () => {
  const { openPopup } = useContactPopup();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Select 5 testimonials for this service page
  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Founder & CEO, TechFlow Solutions',
      content: 'Trademark Assist made trademark registration effortless. Their team handled everything professionally, from the initial search to final approval. We received our certificate in just 5 months—much faster than expected. The comprehensive search report gave us complete confidence before filing.',
      rating: 5
    },
    {
      name: 'Priya Sharma',
      role: 'Marketing Director, GreenLeaf Organics',
      content: 'Before launching our rebrand, we needed certainty that our new name was available. Trademark Assist\'s comprehensive search uncovered a potential conflict we\'d completely missed. They saved us from a costly legal nightmare and helped us pivot to a stronger brand name.',
      rating: 5
    },
    {
      name: 'Emma Thompson',
      role: 'E-commerce Manager, Heritage Home Goods',
      content: 'Getting into Amazon Brand Registry seemed impossible until we found Trademark Assist. They guided us through trademark registration specifically for Amazon\'s requirements, then handled the Brand Registry enrollment process. Our sales increased 40% thanks to the added protection.',
      rating: 5
    },
    {
      name: 'David Williams',
      role: 'Managing Director, Elite Fashion Group',
      content: 'We had a complex international trademark portfolio that needed management. Trademark Assist provided expert guidance on multi-jurisdiction filings and helped us navigate the complexities of EU and UK trademark law post-Brexit. Their attention to detail is unmatched.',
      rating: 4.5
    },
    {
      name: 'Raj Patel',
      role: 'Founder, GreenTech Innovations',
      content: 'Trademark Assist has been our trusted partner for over three years. They\'ve successfully registered multiple trademarks for our expanding product line and provided invaluable advice on brand protection strategy. Their expertise has been instrumental in our growth.',
      rating: 5
    }
  ];

  const getVisibleTestimonials = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const testimonialsPerView = isMobile ? 1 : 3;
    
    const visible = [];
    for (let i = 0; i < testimonialsPerView; i++) {
      const index = (currentTestimonial + i) % testimonials.length;
      visible.push({ ...testimonials[index], originalIndex: index });
    }
    return visible;
  };

  const nextTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev + step) % testimonials.length);
  };

  const prevTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev - step + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      nextTestimonial();
    }, 6000);

    return () => {
      clearInterval(testimonialInterval);
    };
  }, [currentTestimonial]);

  const steps = [
    {
      number: '01',
      title: 'Information Collection',
      description: 'We gather all necessary details about your trademark, including your brand name, logo, and the classes of goods or services you want to protect. Our team ensures we have everything needed for a comprehensive application.'
    },
    {
      number: '02',
      title: 'Research and Search Report',
      description: 'Our experts conduct an extensive search of existing trademarks in the UK IPO database and beyond. We identify potential conflicts and provide you with a detailed report, giving you confidence before filing your application.'
    },
    {
      number: '03',
      title: 'Application and Submission',
      description: 'We prepare your trademark application with precision, ensuring proper classification and descriptions. Once approved by you, we submit it to the UK IPO and monitor its progress, handling any office actions or queries that may arise.'
    }
  ];

  const usps = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Unmatched Success Rate',
      description: 'Our 98% approval rate speaks for itself. We achieve this through meticulous pre-filing searches, expert application preparation, and deep understanding of IPO requirements. Most firms struggle with rejection rates, but our comprehensive process ensures your application is filed correctly the first time.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Expert Legal Team',
      description: 'Our trademark attorneys bring over 15 years of combined experience protecting UK businesses. They\'re not just legally qualified—they\'re specialists who understand the nuances that make or break trademark applications and have successfully navigated thousands of complex IP scenarios.'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: '24/7 Support',
      description: 'From your first inquiry to final registration and beyond, we\'re with you every step. Our round-the-clock support means you never worry about application status or next steps. We handle IPO communications, respond to office actions, and keep you informed throughout the entire journey.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Comprehensive Protection',
      description: 'We don\'t just file paperwork—we build protection strategies. Our service includes thorough searches, proper classification across multiple classes if needed, and guidance on strengthening your mark. We ensure your trademark registration provides maximum protection for your brand.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4 bg-primary text-primary-foreground">Professional Trademark Registration Service</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Trademark Registration Made Simple and Secure
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Protect your brand identity with our expert trademark registration service. We handle everything from comprehensive searches to IPO filing, ensuring your trademark application succeeds. With a 98% approval rate and over 10,000 trademarks registered, we transform the complex legal process into a straightforward journey. Your brand deserves exclusive protection—let us secure it for you at just £350 yearly.
              </p>
              
              {/* CTA Button */}
              <div className="flex gap-4">
                <Button 
                  onClick={openPopup}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground group"
                >
                  Register Your Trademark
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>

            {/* Right Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <ServiceForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Service Section */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop"
                alt="Trademark Registration Service"
                className="rounded-lg shadow-elegant-xl w-full h-auto"
              />
            </motion.div>

            {/* Right Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                What Is Trademark Registration?
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Trademark registration is the legal process of securing exclusive rights to your brand name, logo, or other distinctive marks with the UK Intellectual Property Office (IPO). A registered trademark gives you the power to prevent others from using confusingly similar marks, take legal action against infringers, and build significant brand equity. It's not just a legal formality—it's your first line of defense in protecting what makes your business unique. Our comprehensive registration service handles every step, from conducting thorough searches to ensure your mark is available, through to application submission and responding to any IPO queries. With trademark protection lasting 10 years (and renewable indefinitely), you're investing in long-term security for your brand's future.
              </p>
              
              {/* CTA Button */}
              <div className="mt-8">
                <Button 
                  onClick={openPopup}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground group"
                  size="lg"
                >
                  Register Your Trademark
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                How We Work: Our Trademark Registration Process
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                We've streamlined trademark registration into three clear steps. Our proven process eliminates confusion and uncertainty, giving you complete confidence as we protect your brand. From initial consultation to final approval, we handle everything while keeping you informed at every stage.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-8 h-full border-2 border-transparent hover:border-primary transition-all duration-300 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 text-8xl font-bold text-primary/5 group-hover:text-primary/10 transition-colors duration-300">
                    {step.number}
                  </div>
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-primary-foreground font-bold text-2xl mb-6 shadow-lg">
                      {step.number}
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-4">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Transparent Pricing for Every Business Need
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              No surprises, no hidden costs. Choose the package that fits your business stage and budget. Every tier includes expert support, comprehensive guidance, and our commitment to your brand's protection.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
            {/* Trademark Watch */}
            <Card className="p-6 border-2 border-border hover:border-primary hover:shadow-glow transition-all duration-500 flex flex-col relative overflow-hidden group bg-gradient-to-br from-background to-accent/30">
              {/* Fill hover effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">Trademark Watch</h3>
                <div className="text-3xl font-bold text-primary mb-4 group-hover:scale-110 transition-transform duration-300">£350<span className="text-base text-muted-foreground">/year</span></div>
              </div>
              <div className="space-y-3 mb-6 flex-1 overflow-y-auto max-h-[250px] custom-scrollbar relative z-10">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Ongoing trademark monitoring and protection</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Case review and filing assistance</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Basic trademark search</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Online filing support</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Trademark security assurance</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">24/7 monitoring for infringements</span>
                </div>
              </div>
              <Button onClick={openPopup} variant="outline" className="w-full mt-auto border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground hover:shadow-lg transition-all duration-300 relative z-10">
                Start Monitoring
              </Button>
            </Card>

            {/* Basic Registration */}
            <Card className="p-6 border-2 border-border hover:border-secondary hover:shadow-glow-red transition-all duration-500 flex flex-col relative overflow-hidden group bg-gradient-to-br from-background to-accent/30">
              {/* Fill hover effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-secondary transition-colors duration-300">Basic Registration</h3>
                <div className="text-3xl font-bold text-secondary mb-1 group-hover:scale-110 transition-transform duration-300">from £299</div>
                <div className="text-sm text-muted-foreground mb-4">+ £170 IPO Govt Fee</div>
              </div>
              <div className="space-y-3 mb-6 flex-1 overflow-y-auto max-h-[250px] custom-scrollbar relative z-10">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Complete case review and filing</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Direct hit trademark search</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Trademark alert system</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">High satisfaction guarantee</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Refusal risk assessment</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground font-semibold">Money-back guarantee</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Dedicated case manager</span>
                </div>
              </div>
              <Button onClick={openPopup} className="w-full mt-auto bg-secondary hover:bg-secondary-light hover:shadow-lg transition-all duration-300 relative z-10">
                Choose Basic
              </Button>
            </Card>

            {/* Premium Registration */}
            <Card className="p-6 border-4 border-primary relative hover:shadow-2xl hover:scale-105 transition-all duration-500 flex flex-col overflow-visible group bg-gradient-to-br from-primary/5 via-background to-secondary/5">
              <Badge className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-lg animate-pulse z-10">⭐ Most Popular</Badge>
              
              {/* Fill hover effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">Premium Registration</h3>
                <div className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-1 group-hover:scale-110 transition-transform duration-300">from £499</div>
                <div className="text-sm text-muted-foreground mb-4">+ £170 IPO Govt Fee</div>
              </div>
              <div className="space-y-3 mb-6 flex-1 overflow-y-auto max-h-[250px] custom-scrollbar relative z-10">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground font-semibold">Everything in Basic package</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Comprehensive search report</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">High priority application handling</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">95%+ approval rate</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Cease & desist letter drafting</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground font-semibold">Free 30-minute consultation</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Expedited processing</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Office action response assistance</span>
                </div>
              </div>
              <Button onClick={openPopup} className="w-full mt-auto bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300 relative z-10">
                Choose Premium
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Why Choose Trademark Assist for Your Registration
              </h2>
              <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                With 15+ years of expertise, 10,000+ trademarks registered, and a 98% approval rate, we combine legal precision with business understanding to protect your brand successfully.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {usps.map((usp, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-8 border-2 border-border hover:border-primary/50 transition-all duration-500 h-full group relative overflow-hidden bg-white hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5 hover:shadow-[0_20px_60px_-15px_rgba(1,33,105,0.3)] hover:scale-[1.02]">
                  {/* Animated gradient wave background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
                  
                  {/* Glowing corner accents */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -mr-16 -mt-16"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-secondary/20 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -ml-16 -mb-16"></div>
                  
                  <div className="relative z-10">
                    {/* Enhanced icon with 3D effect */}
                    <div className="relative w-20 h-20 mb-6 group-hover:mb-8 transition-all duration-500">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
                      <div className="relative w-20 h-20 bg-gradient-to-br from-primary/10 to-secondary/10 group-hover:from-primary group-hover:to-secondary rounded-2xl flex items-center justify-center text-primary group-hover:text-white transition-all duration-500 group-hover:rotate-[360deg] group-hover:scale-110 shadow-lg group-hover:shadow-2xl">
                        {usp.icon}
                      </div>
                    </div>
                    
                    {/* Title with slide and color effect */}
                    <h3 className="text-2xl font-semibold text-foreground mb-4 group-hover:text-primary transition-all duration-500 group-hover:tracking-wide">
                      {usp.title}
                    </h3>
                    
                    {/* Description with fade effect */}
                    <p className="text-muted-foreground leading-relaxed group-hover:text-foreground/90 transition-all duration-500">
                      {usp.description}
                    </p>
                    
                    {/* Animated bottom accent bar */}
                    <div className="mt-6 h-1 w-0 group-hover:w-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-700 ease-out"></div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
          
          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              onClick={openPopup}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group"
              size="lg"
            >
              Get Started Now
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section with Background Image */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&h=600&fit=crop"
            alt="Start Your Trademark Registration"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary-dark/90 to-secondary/85"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-primary-foreground"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Protect Your Brand? Start Your Trademark Registration Today from £299
            </h2>
            <p className="text-lg mb-8 text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto">
              Don't wait until someone copies your brand. Secure your trademark now and protect what you've built. Our expert team is ready to handle your application from start to finish. With our basic package starting from just £299 + IPO fees, you get professional handling and the peace of mind that comes with a 98% success rate. Click below to get started—your brand deserves exclusive protection.
            </p>
            <Button 
              onClick={openPopup} 
              size="lg" 
              variant="secondary" 
              className="text-lg px-8 bg-primary-foreground text-primary hover:bg-primary-foreground/90 hover:scale-105 transition-all duration-300 shadow-elegant-xl"
            >
              Start My Trademark Registration Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              What Our Clients Say About Our Trademark Service
            </h2>
          </div>

          <div className="relative">
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
              aria-label="Previous testimonials"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
              aria-label="Next testimonials"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <div className="overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="grid grid-cols-1 md:grid-cols-3 gap-6"
                >
                  {getVisibleTestimonials().map((testimonial, index) => (
                    <div key={`${testimonial.originalIndex}-${index}`}>
                      <TestimonialCard testimonial={testimonial} />
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index * 3)}
                  className={`h-2 rounded-full transition-all duration-500 ease-out ${
                    Math.floor(currentTestimonial / 3) === index
                      ? 'bg-primary w-8 shadow-glow' 
                      : 'bg-border w-2 hover:bg-primary/50 hover:w-4'
                  }`}
                  aria-label={`View testimonial group ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Frequently Asked Questions About Trademark Registration
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">
                How long does the trademark registration process take?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                The typical trademark registration timeline in the UK is 4-6 months, though this can vary depending on several factors. After we submit your application to the UK IPO, it undergoes an initial examination (usually 1-2 weeks), followed by publication in the Trade Marks Journal for a 2-month opposition period. If no oppositions are filed and there are no objections from the examiner, your trademark proceeds to registration. We monitor your application throughout this process and handle any correspondence with the IPO, keeping you informed of progress at every stage.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">
                What's included in your trademark search report?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Our comprehensive trademark search goes far beyond a simple database query. We search the UK IPO register for identical and similar marks, analyze common law trademarks that might conflict with yours, check company names at Companies House, review domain name registrations, and examine relevant international trademarks that could affect UK rights. Our detailed report categorizes potential conflicts by risk level (high, medium, low), provides visual examples of similar marks, explains why certain marks may pose conflicts, and offers strategic recommendations on how to proceed. This thorough approach is why we achieve a 98% approval rate—we identify potential problems before filing.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">
                Do I need to register in multiple classes?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                This depends on your business activities and future plans. Trademarks are registered for specific categories (classes) of goods or services—there are 45 classes in total. If you only sell clothing, you might only need Class 25. However, if you also plan to open retail stores (Class 35) or offer online retail services (also Class 35), you should register in those classes too. We help you determine the right classes during our initial consultation by understanding your current business and future expansion plans. Registering in additional classes now is more cost-effective than filing separate applications later, and it provides broader protection against competitors who might try to use your brand in adjacent industries.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">
                What happens if someone opposes my trademark application?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                If someone files an opposition during the 2-month publication period, don't panic—this happens more often than you'd think, and we're experts at handling oppositions. We first assess the strength of their opposition and your position. Many oppositions are based on weak grounds and can be overcome with a well-crafted response. We'll present you with strategic options: proceeding with a full defense (included in our Premium package, additional cost for Basic/Standard), negotiating a coexistence agreement with the opponent, or in rare cases, amending your application to address their concerns. Throughout this process, you're never alone—we handle all correspondence, legal arguments, and negotiations on your behalf. Our experience means we know how to position your case for the best possible outcome.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">
                Can I register a trademark internationally, or is this just for the UK?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Our £350 yearly service covers UK trademark registration through the UK IPO, which protects your mark throughout the United Kingdom. However, if you're doing business internationally or plan to expand abroad, you'll need protection in those countries too. We can help you with international trademark registration through several routes: the Madrid Protocol (allows you to file in multiple countries through a single application), direct filings in the EU (EUIPO registration covers all EU member states), or individual country applications. Each approach has different costs and strategic considerations. We'll assess your international business plans and recommend the most cost-effective protection strategy. Many clients start with UK protection and expand internationally as their business grows—we're here to guide you through every stage.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default TrademarkRegistrationPage;
